import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import store from "../store";
import { wsManager } from "../websocket/wsManager";
import { bootstrapApp } from "../actions/bootstrapActions";
import { selectAccessToken } from "../selectors/userSelectors";
import { PRESENCE_CLEAR } from "../features/presence/model/presenceConstants";
import Loader from "../components/Loader";

/**
 * Authenticated product runtime boundary (CLIENT-PRESENTATION-LINKS-C1).
 *
 * Owns the existing bootstrap splash gate, access-token subscription and
 * WebSocket lifecycle. Public `/p/:token` and `/login` are Router siblings
 * and never mount this boundary. Product trees nest RequireAuthenticatedSession
 * inside this splash so recoverable refresh can finish before the auth gate.
 */
function AuthenticatedProductRuntime() {
  // App websocket lifecycle depends on selectAccessToken primitive identity semantics.
  const accessToken = useSelector(selectAccessToken);
  const bootstrapPhase = useSelector((state) => state.bootstrap?.phase);
  const dispatch = useDispatch();

  // Phase 2 — one-shot bootstrap on mount. Coalesced inside the thunk so
  // React StrictMode's double-mount in dev does not fire two profile fetches.
  useEffect(() => {
    dispatch(bootstrapApp());
  }, [dispatch]);

  useEffect(() => {
    if (!accessToken) return;

    wsManager.connect(dispatch, store.getState);
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (!accessToken) {
      wsManager.disconnect();
      dispatch({ type: PRESENCE_CLEAR });
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    return () => {
      wsManager.disconnect();
      dispatch({ type: PRESENCE_CLEAR });
    };
  }, [dispatch]);

  // Splash gate — block route mounting until the bootstrap attempt finishes
  // (success, failure, or no-auth — all flip the phase to 'ready'). This
  // prevents the redirect-flicker class of bugs where useOnboardingRedirect
  // fires before profile data is even attempted to be loaded.
  if (bootstrapPhase !== "ready") {
    return <Loader />;
  }

  return <Outlet />;
}

export default AuthenticatedProductRuntime;
